import { Button } from '@roko/ui/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@roko/ui/components/ui/sheet'
import { Switch } from '@roko/ui/components/ui/switch'
import type { TFeatureFlags } from '@roko/ui/types/common'
import { IconCode } from '@tabler/icons-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LOCAL_STORAGE_KEY, useFeatureFlags } from '@/context/FeatureProvider'

/**
 * A development panel component for viewing and overriding feature flags locally.
 *
 * This panel allows developers to toggle feature flags for testing purposes.
 * Overrides are stored in localStorage under the key "devFeatureFlags".
 * When the panel is closed, the page reloads to apply any changes.
 *
 * Features:
 * - Displays all available feature flags.
 * - Allows toggling each flag's enabled state.
 * - Persists overrides in localStorage.
 * - Provides a button to reset all overrides and reload the page.
 *
 * @returns {JSX.Element} The rendered FeatureFlagDevPanel component.
 */

export function FeatureFlagDevPanel() {
  const flags = useFeatureFlags()
  const [overrides, setOverrides] = useState<TFeatureFlags>(JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}'))
  const { t } = useTranslation()

  const toggleFlag = (key: string) => {
    const prev = overrides[key] ?? flags[key]
    const updated = {
      ...overrides,
      [key]: { ...prev, enabled: !(prev.enabled ?? false) },
    }
    setOverrides(updated)
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated))
  }

  const handleFeatureRefresh = (open: boolean) => {
    if (!open) window.location.reload()
  }

  const clearLocalStorage = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY)
    setOverrides({})
    window.location.reload()
  }

  return (
    <Sheet onOpenChange={handleFeatureRefresh}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="fixed bottom-4 right-4">
          <IconCode />
          {t('featureDevPanel.button')}
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{t('featureDevPanel.title')}</SheetTitle>
          <SheetDescription>{t('featureDevPanel.description')}</SheetDescription>
        </SheetHeader>

        <div className="p-4 space-y-3">
          {Object.entries(flags).map(([key, flag]) => (
            <div key={key} className="flex items-center justify-between">
              <span>{key}</span>
              <Switch checked={overrides[key]?.enabled ?? flag.enabled} onCheckedChange={() => toggleFlag(key)} />
            </div>
          ))}

          <Button variant="destructive" size="sm" onClick={clearLocalStorage}>
            {t('featureDevPanel.reset')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
